import { Button, Card, Flex } from '@servicetitan/anvil2';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AmountField, Form, LoanFormData } from './Form';
import { Display } from './Display';
import { FinancedAmountStore } from './financedAmountStore';
import { AmountRangeStore } from './amountRangeStore';
import { SubmitHandler } from 'react-hook-form';
import Edit from '@servicetitan/anvil2/assets/icons/material/round/edit.svg';
import { toNumber } from '@servicetitan/anvil2-react-hook-form';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { CustomPlansStore } from '../plans/CustomPlansStore';
import { buildAmountRangeContextKey, buildAmountRangeSearchKey } from '../plans/amountRange';

interface ContainerProps {
    showProviderSelection?: boolean;
    /** Maximum allowed financed amount (e.g., approved loan amount for update offer flow) */
    maxAmount?: number;
    amountRangeEnabled?: boolean;
}

const LegacyContainer = ({ showProviderSelection, maxAmount }: ContainerProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const { setFinancedAmount } = FinancedAmountStore.useStore();

    const onSubmit: SubmitHandler<LoanFormData> = data => {
        setFinancedAmount({
            saleAmount: toNumber(data.saleAmount),
            downPayment: toNumber(data.downPayment),
        });
        setIsEditing(false);
    };
    return (
        <Card style={{ width: '100%' }}>
            {isEditing ? (
                <Flex
                    justifyContent="space-between"
                    sm={{ direction: 'column' }}
                    lg={{ direction: 'row' }}
                    style={{ width: '100%' }}
                >
                    <Form
                        onSubmit={onSubmit}
                        onCancel={() => setIsEditing(false)}
                        showProviderSelection={showProviderSelection}
                        maxAmount={maxAmount}
                    />
                </Flex>
            ) : (
                <Flex justifyContent="space-between" style={{ width: '100%' }}>
                    <Display showProviderSelection={showProviderSelection} />
                    <Flex alignSelf="baseline">
                        <Button
                            onClick={() => setIsEditing(true)}
                            icon={Edit}
                            appearance="ghost"
                            aria-label="Edit Financing Information"
                        />
                    </Flex>
                </Flex>
            )}
        </Card>
    );
};

interface AmountRangeSubmission {
    generation: number;
    contextKey: string;
    searchKey: string;
    lastChangedField: AmountField;
}

const AmountRangeContainer = ({ showProviderSelection, maxAmount }: ContainerProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [lastChangedField, setLastChangedField] = useState<AmountField>('saleAmount');
    const [submission, setSubmission] = useState<AmountRangeSubmission | null>(null);
    const generation = useRef(0);
    const { setFinancedAmount } = FinancedAmountStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const {
        customPlanIds,
        hasCustomPlans,
        provider: providerFromUrl,
    } = CustomPlansStore.useStore();
    const { status, message, searchKey } = AmountRangeStore.useStore();
    const customPlans =
        providerFromUrl === setupData?.provider && hasCustomPlans ? customPlanIds : null;
    const contextInput = {
        provider: setupData?.provider,
        tenantId: setupData?.tenantId,
        customerId: setupData?.customerId,
        buId: setupData?.buId,
        state: setupData?.state,
        customPlanIds: customPlans,
    };
    const contextKey = buildAmountRangeContextKey(contextInput);

    const activeResultMatches =
        submission?.contextKey === contextKey && submission.searchKey === searchKey;
    const rangeError =
        activeResultMatches && status === 'out-of-range' && message != null
            ? { field: submission.lastChangedField, message: message.headline }
            : null;

    useEffect(() => {
        if (!activeResultMatches || submission == null || status == null) {
            return;
        }

        if (status !== 'loading' && status !== 'out-of-range') {
            setIsEditing(false);
            setSubmission(null);
        }
    }, [activeResultMatches, status, submission]);

    useEffect(() => {
        if (submission != null && submission.contextKey !== contextKey) {
            setSubmission(null);
        }
    }, [contextKey, submission]);

    const invalidateSubmission = useCallback((field: AmountField) => {
        setLastChangedField(field);
        setSubmission(null);
    }, []);

    const onSubmit: SubmitHandler<LoanFormData> = data => {
        const saleAmount = toNumber(data.saleAmount);
        const downPayment = toNumber(data.downPayment);
        const financedAmount = saleAmount - downPayment;
        generation.current += 1;
        setSubmission({
            generation: generation.current,
            lastChangedField,
            contextKey,
            searchKey: buildAmountRangeSearchKey({ ...contextInput, financedAmount }),
        });
        setFinancedAmount({ saleAmount, downPayment });
    };

    const cancel = useCallback(() => {
        setSubmission(null);
        setIsEditing(false);
    }, []);

    return (
        <Card style={{ width: '100%' }}>
            {isEditing ? (
                <Flex
                    justifyContent="space-between"
                    sm={{ direction: 'column' }}
                    lg={{ direction: 'row' }}
                    style={{ width: '100%' }}
                >
                    <Form
                        onSubmit={onSubmit}
                        onCancel={cancel}
                        showProviderSelection={showProviderSelection}
                        maxAmount={maxAmount}
                        amountRangeError={rangeError}
                        onAmountChange={invalidateSubmission}
                    />
                </Flex>
            ) : (
                <Flex justifyContent="space-between" style={{ width: '100%' }}>
                    <Display showProviderSelection={showProviderSelection} />
                    <Flex alignSelf="baseline">
                        <Button
                            onClick={() => setIsEditing(true)}
                            icon={Edit}
                            appearance="ghost"
                            aria-label="Edit Financing Information"
                        />
                    </Flex>
                </Flex>
            )}
        </Card>
    );
};

export const Container = ({ amountRangeEnabled = false, ...props }: ContainerProps) =>
    amountRangeEnabled ? <AmountRangeContainer {...props} /> : <LegacyContainer {...props} />;
